import { ConfirmPopup } from '@neo/blocks/confirm-popup'
import { Button } from '@neo/primitives/button'
import { Loader } from '@neo/primitives/loader'
import { Select, type SelectOption } from '@neo/primitives/select'
import type { FlowConfig } from '../types'
import {
  type AgentNumberRow,
  attachBlockedNote,
  type NumberUsage,
  numberUsage,
} from './agent-phone-numbers.utils'
import { useAgentPhoneNumbers } from './use-agent-phone-numbers'

interface AgentPhoneNumbersProps {
  agentId: string
  // The saved agent, which decides whether core will attach numbers to it yet.
  savedAgent?: FlowConfig | null
  canEdit: boolean
}

const usageHint = (usage: NumberUsage) => {
  if (usage.kind === 'app') return 'Connected to another app'
  if (usage.kind !== 'in_use') return null
  return usage.agentName ? `Used by ${usage.agentName}` : 'Used by another agent'
}

const NumberOptionLabel = ({ row, usage }: { row: AgentNumberRow; usage: NumberUsage }) => {
  const hint = usageHint(usage)
  return (
    <span className="flex w-full items-center justify-between gap-3">
      <span className="truncate">
        {row.number}
        {row.name_tag && <span className="ml-2 text-secondary">{row.name_tag}</span>}
      </span>
      {hint && <span className="shrink-0 text-xs-400 text-secondary">{hint}</span>}
    </span>
  )
}

interface AttachedNumbersProps {
  rows: AgentNumberRow[]
  isPending: boolean
  isError: boolean
  onRetry: () => void
  onRemove?: (row: AgentNumberRow) => void
  removeDisabled: boolean
}

const AttachedNumbers = ({
  rows,
  isPending,
  isError,
  onRetry,
  onRemove,
  removeDisabled,
}: AttachedNumbersProps) => {
  if (isPending) return <Loader variant="inline" text="Loading phone numbers..." />
  if (isError) {
    return (
      <div className="flex items-center gap-2 text-s-400 text-secondary">
        Couldn't load phone numbers.
        <Button
          variant="link"
          size="sm"
          onClick={onRetry}
          data-testid="agent-settings-numbers-retry-btn"
        >
          Try again
        </Button>
      </div>
    )
  }
  if (!rows.length) {
    return (
      <p className="text-s-400 text-secondary" data-testid="agent-settings-numbers-empty">
        No phone numbers attached.
      </p>
    )
  }
  return (
    <ul
      className="divide-y divide-border rounded-md border border-border"
      data-testid="agent-settings-numbers-list"
    >
      {rows.map((row) => (
        <li key={row.id} className="flex items-center justify-between gap-3 px-3 py-2">
          <div className="min-w-0">
            <div className="text-p-400 text-foreground">{row.number}</div>
            {row.name_tag && (
              <div className="truncate text-s-400 text-secondary">{row.name_tag}</div>
            )}
          </div>
          {onRemove && (
            <Button
              variant="ghost"
              size="sm"
              disabled={removeDisabled}
              onClick={() => onRemove(row)}
              aria-label={`Remove ${row.number}`}
              data-testid="agent-settings-numbers-remove-btn"
            >
              Remove
            </Button>
          )}
        </li>
      ))}
    </ul>
  )
}

// Embed only. Read-only sessions see the list without controls (hodor refuses their PATCH).
export const AgentPhoneNumbers = ({ agentId, savedAgent, canEdit }: AgentPhoneNumbersProps) => {
  const blockedNote = attachBlockedNote(savedAgent)
  const {
    numbersQuery,
    attached,
    candidates,
    isSearching,
    setSearch,
    isSaving,
    attach,
    remove,
    pendingMove,
    isMoveOpen,
    setMoveOpen,
    confirmMove,
  } = useAgentPhoneNumbers(agentId)

  const options: SelectOption[] = candidates.map((row) => {
    const usage = numberUsage(row, agentId)
    return {
      value: row.id,
      label: <NumberOptionLabel row={row} usage={usage} />,
      searchValue: [row.number, row.name_tag].filter(Boolean).join(' '),
      // Shown, not offered: core refuses a number an app or trunk answers.
      disabled: isSaving || usage.kind === 'app',
    }
  })

  // The select's disabled state is visual only (no disabled attribute), so a keyboard user can
  // still open it; check again before sending.
  const handlePick = (value: string | string[]) => {
    const row = candidates.find((candidate) => candidate.id === value)
    if (row && !blockedNote && numberUsage(row, agentId).kind !== 'app') attach(row)
  }

  return (
    <section
      className="mt-4 rounded-lg border border-border bg-card px-4"
      data-testid="section-agent-phone-numbers"
    >
      <div className="py-4">
        <div className="text-p-600 text-foreground">Phone numbers</div>
        <div className="mt-1 max-w-2xl text-s-400 text-secondary">
          Calls to these numbers are answered by this agent.
        </div>
      </div>
      <div className="flex flex-col gap-3 border-t border-border py-4">
        <AttachedNumbers
          rows={attached}
          isPending={numbersQuery.isPending}
          isError={numbersQuery.isError}
          onRetry={() => numbersQuery.refetch()}
          onRemove={canEdit ? remove : undefined}
          removeDisabled={isSaving}
        />
        {canEdit && (
          <>
            <div className="max-w-md" data-testid="agent-settings-numbers-select">
              <Select
                options={options}
                value=""
                onChange={handlePick}
                onSearchChange={setSearch}
                placeholder="Add a phone number"
                inputPlaceholder="Search by number or name"
                emptyPlaceholder={isSearching ? 'Searching...' : 'No phone numbers found.'}
                loading={numbersQuery.isPending}
                disabled={!!blockedNote || isSaving}
              />
            </div>
            {blockedNote && (
              <p
                className="text-s-400 text-secondary"
                data-testid="agent-settings-numbers-unpublished-note"
              >
                {blockedNote}
              </p>
            )}
          </>
        )}
      </div>
      <ConfirmPopup
        open={isMoveOpen}
        setOpen={setMoveOpen}
        title="Move phone number?"
        description={
          pendingMove
            ? `${pendingMove.row.number} answers for '${pendingMove.agentName}'. Move it to this agent?`
            : ''
        }
        okButtonProps={{
          btnTxt: 'Move number',
          onClick: confirmMove,
          'data-testid': 'agent-settings-numbers-replace-confirm-btn',
        }}
        cancelButtonProps={{ 'data-testid': 'agent-settings-numbers-replace-cancel-btn' }}
      />
    </section>
  )
}
